import React, { useState, useEffect } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { Price } from 'interfaces/price'
import { StripeProduct } from 'interfaces/stripe-product'
import {
  ProductName,
  ProductUsageType,
  BillingPeriod,
} from 'interfaces/purchase-order'

const LINQ_BLUE_PRODUCT_ID = 'prod_RQjmrtiHgXI5PS' // Sales Enablement
const LINQ_ONE_PRODUCT_ID = 'prod_QmwEcZEeTODmrW' // One
const LINQ_SAPPHIRE_PRODUCT_ID = 'prod_Sus3q8anSlxfpM'
const PARTNER_API_SUPPORT_PRODUCT_ID = 'prod_SbOyRTEsvjBl69'
const TEXTING_BETTY_PRODUCT_ID = 'prod_T4DXFwfjCxxm3o'
const LINQ_FIND_MY_PRODUCT_ID = 'prod_UVfcDzSYXwIvX0'
const LINQ_INFRA_PRODUCT_ID = 'prod_TI8X1aY7VApKAs' // Infrastructure
const LINQ_INFRA_200_CONTACT_PRODUCT_ID = 'prod_V0ouQMnfLdZU3s'

interface ProductBundleModalProps {
  show: boolean
  onHide: () => void
  productName: ProductName
  productColor: string
  baseProduct: StripeProduct | null
  addonProducts: StripeProduct[]
  additionalProducts: StripeProduct[]
  prices: { [productId: string]: Price[] }
  isLoading?: boolean
  isEditMode?: boolean
  existingItems?: {
    priceId: string
    quantity: number
    productId: string
  }[]
  onAddProducts: (
    selectedItems: {
      priceId: string
      quantity: number
      productId: string
      discount?: number
    }[]
  ) => void
}

interface PriceWithProduct {
  price: Price
  product: StripeProduct
  productType: ProductUsageType
}

const ProductBundleModal: React.FC<ProductBundleModalProps> = ({
  show,
  onHide,
  productName,
  productColor,
  baseProduct,
  addonProducts,
  additionalProducts,
  prices,
  isLoading = false,
  isEditMode = false,
  existingItems = [],
  onAddProducts,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('monthly')
  const [quantities, setQuantities] = useState<{ [priceId: string]: number }>(
    {}
  )
  const [activeProducts, setActiveProducts] = useState<Set<string>>(new Set())
  const [addonOrder, setAddonOrder] = useState<string[]>([]) // Track addon selection order
  const [activeSapphirePriceId, setActiveSapphirePriceId] = useState<
    string | null
  >(null)
  const [activeTextingBettyPriceId, setActiveTextingBettyPriceId] = useState<
    string | null
  >(null)
  const [activeFindMyPriceId, setActiveFindMyPriceId] = useState<string | null>(
    null
  )
  // Helper function to get lighter shade of color for inactive state
  const getLighterColor = (hexColor: string, opacity: number = 0.4): string => {
    // Remove # if present
    const hex = hexColor.replace('#', '')

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    // Return rgba with opacity
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  // Get specific inactive background color based on product color
  const getInactiveBackgroundColor = (productColor: string): string => {
    switch (productColor) {
      case '#2563eb': // Sales Enablement blue
        return '#dbeafe'
      case '#9333ea': // Reseller purple
        return '#f3e8ff' // Light lavender
      case '#0ca5e9': // Infrastructure cyan
        return '#cffafe' // Light cyan
      case '#0891b2': // Infras 200 Contacts teal
        return '#cffafe' // Light cyan
      case '#10b981': // One green
        return getLighterColor(productColor, 0.15) // Keep generated for One
      default:
        return getLighterColor(productColor, 0.15)
    }
  }

  // Tiered addon products config - products with multiple selectable price tiers
  const tieredAddonProducts: {
    [productId: string]: {
      tiers: { priceId: string; label: string }[]
      activeState: string | null
      setActiveState: (priceId: string | null) => void
    }
  } = {
    [LINQ_SAPPHIRE_PRODUCT_ID]: {
      tiers: [
        {
          priceId: 'price_1SQyYsH5ocKrGenqOOSFsBor',
          label: 'Sapphire - $300/month',
        },
        {
          priceId: 'price_1S99mQH5ocKrGenq9lw3qDA3',
          label: 'Sapphire - $1,000',
        },
        {
          priceId: 'price_1T867zH5ocKrGenqv58D21uN',
          label: 'Sapphire - $3,000',
        },
      ],
      activeState: activeSapphirePriceId,
      setActiveState: setActiveSapphirePriceId,
    },
    [TEXTING_BETTY_PRODUCT_ID]: {
      tiers: [
        { priceId: 'price_1S856jH5ocKrGenq9fsFfmp3', label: 'TB + AI Agent' },
        {
          priceId: 'price_1SdCLyH5ocKrGenqofDSnfLr',
          label: 'TB FUB Integration',
        },
      ],
      activeState: activeTextingBettyPriceId,
      setActiveState: setActiveTextingBettyPriceId,
    },
    [LINQ_FIND_MY_PRODUCT_ID]: {
      tiers: [
        {
          priceId: 'price_1TWeHcH5ocKrGenq89scw3II',
          label: 'Find My - $79/month',
        },
        {
          priceId: 'price_1TWi8fH5ocKrGenqdpfoAa0W',
          label: 'Find My - $316/quarterly',
        },
        {
          priceId: 'price_1TWiAOH5ocKrGenqCzB9aIDY',
          label: 'Find My - $474/semiannually',
        },
        {
          priceId: 'price_1TWiBIH5ocKrGenqceWfDUKf',
          label: 'Find My - $948/year',
        },
      ],
      activeState: activeFindMyPriceId,
      setActiveState: setActiveFindMyPriceId,
    },
  }

  // Helper to create price config that appears in all periods
  const createAllPeriodsConfig = (priceId: string) => ({
    monthly: [priceId],
    quarterly: [priceId],
    semi_annual: [priceId],
    yearly: [priceId],
  })

  // Default price IDs to show for specific products by billing period
  const defaultPriceIds: {
    [productId: string]: { [key in BillingPeriod]?: string[] }
  } = {
    // Sales Enablement default prices
    prod_RQjmrtiHgXI5PS: {
      monthly: ['price_1TJGJNH5ocKrGenqS2KTaC5H'],
      quarterly: ['price_1TLpUFH5ocKrGenqPigjN6AO'],
      semi_annual: ['price_1TLpUZH5ocKrGenqp2zzXzge'],
      yearly: ['price_1TLpUsH5ocKrGenqYtPV2Oqj'],
    },
    // Infrastructure default prices
    prod_TI8X1aY7VApKAs: {
      monthly: ['price_1TJFkuH5ocKrGenqd51d2E6d'],
      quarterly: ['price_1TLpS8H5ocKrGenqepyQEiqR'],
      semi_annual: ['price_1TLpSgH5ocKrGenqyV96c1Lq'],
      yearly: ['price_1TLpT2H5ocKrGenqI3CEawRY'],
    },
    // Infrastructure 200 contact
    prod_V0ouQMnfLdZU3s: {
      monthly: ['price_1U0nGnH5ocKrGenqzFVTWP7E'],
    },
    // One default prices
    prod_QmwEcZEeTODmrW: {
      monthly: ['price_1PvMMRH5ocKrGenqLAFZeLRf'],
      quarterly: ['price_1SQwzdH5ocKrGenqs9XgQzjn'],
      semi_annual: ['price_1SQx0YH5ocKrGenqhgmz4sqX'],
      yearly: ['price_1SQx1FH5ocKrGenqRyjDQBr2'],
    },
    // International Plan default prices
    prod_S9yd3MQIMENt0t: {
      monthly: ['price_1RJzVFH5ocKrGenqoippfBiZ'],
      quarterly: ['price_1RJzUZH5ocKrGenq5bxzgAbR'],
      semi_annual: ['price_1SQybIH5ocKrGenqRgN32EXd'],
      yearly: ['price_1RIw6UH5ocKrGenq1cd3QGuQ'],
    },
    // Activation Fee default prices
    prod_SY1S8CosHpGm5b: {
      monthly: [''],
      quarterly: [''],
      semi_annual: [],
      yearly: [],
    },
    // One-time products that appear in all periods
    // Provision Fee
    prod_RwYwhl0YljzWPo: createAllPeriodsConfig(
      'price_1REv9mH5ocKrGenqJJUD01je'
    ),
    // Partner API Support
    prod_SbOyRTEsvjBl69: createAllPeriodsConfig(
      'price_1RgCAIH5ocKrGenqcFmG7wCe'
    ),
    // Sapphire add-on default prices
    prod_Sus3q8anSlxfpM: {
      monthly: [
        'price_1SQyYsH5ocKrGenqOOSFsBor',
        'price_1S99mQH5ocKrGenq9lw3qDA3',
      ],
      quarterly: ['price_1S99mQH5ocKrGenq9lw3qDA3'],
      semi_annual: ['price_1S99mQH5ocKrGenq9lw3qDA3'],
      yearly: [
        'price_1S99mQH5ocKrGenq9lw3qDA3',
        'price_1T867zH5ocKrGenqv58D21uN',
      ],
    },
    // Find My add-on default prices
    prod_UVfcDzSYXwIvX0: {
      monthly: ['price_1TWeHcH5ocKrGenq89scw3II'],
      quarterly: ['price_1TWi8fH5ocKrGenqdpfoAa0W'],
      semi_annual: ['price_1TWiAOH5ocKrGenqCzB9aIDY'],
      yearly: ['price_1TWiBIH5ocKrGenqceWfDUKf'],
    },
    // Texting Betty add-on default prices (monthly only)
    prod_T4DXFwfjCxxm3o: {
      monthly: [
        'price_1S856jH5ocKrGenq9fsFfmp3',
        'price_1SdCLyH5ocKrGenqofDSnfLr',
      ],
    },
    // Reseller base product default prices
    prod_UDIqyvLTlQVH54: {
      monthly: ['price_1TlbZbH5ocKrGenqgXgPaQTK'],
      quarterly: ['price_1TEsLiH5ocKrGenqRrmKKBS6'],
      semi_annual: ['price_1TEsN9H5ocKrGenqkgegZKSJ'],
      yearly: ['price_1TEsO9H5ocKrGenqull34gy6'],
    },
  }

  // Group all prices by billing period
  const groupPricesByPeriod = () => {
    const grouped: { [key in BillingPeriod]?: PriceWithProduct[] } = {}

    // Determine which periods the base product supports
    const baseProductPeriods = new Set<BillingPeriod>()
    if (baseProduct) {
      const baseProductDefaultPrices = defaultPriceIds[baseProduct.id]
      if (baseProductDefaultPrices) {
        Object.keys(baseProductDefaultPrices).forEach((period) => {
          baseProductPeriods.add(period as BillingPeriod)
        })
      }
    }

    // Helper to process products with their type
    const processProducts = (
      products: StripeProduct[],
      productType: ProductUsageType
    ) => {
      products.forEach((product) => {
        const productPrices = prices[product.id] || []
        const defaultPricesForProduct = defaultPriceIds[product.id]

        productPrices
          .filter((p) => p.active)
          .filter((p) => {
            // For tiered addon products, only include non-discounted (base) prices
            if (tieredAddonProducts[product.id]) {
              return p.metadata?.is_discount !== 'true'
            }
            return true
          })
          .forEach((price) => {
            let period: BillingPeriod = 'one-time'

            if (price.type === 'recurring' && price.recurring) {
              const { interval, interval_count: intervalCount } =
                price.recurring
              if (interval === 'month') {
                if (intervalCount === 1) {
                  period = 'monthly'
                } else if (intervalCount === 3) {
                  period = 'quarterly'
                } else if (intervalCount === 6) {
                  period = 'semi_annual'
                } else {
                  period = 'monthly'
                }
              } else if (interval === 'year' && intervalCount === 1) {
                period = 'yearly'
              }
            }

            // For one-time addon products, add to ALL billing periods that the base product supports
            if (period === 'one-time' && productType === 'addon') {
              const allPeriods: BillingPeriod[] = (
                [
                  'monthly',
                  'quarterly',
                  'semi_annual',
                  'yearly',
                ] as BillingPeriod[]
              ).filter((p) => baseProductPeriods.has(p))
              allPeriods.forEach((p) => {
                // Check if this price is allowed for each specific period
                if (defaultPricesForProduct) {
                  const allowedPriceIds = defaultPricesForProduct[p]
                  if (allowedPriceIds && !allowedPriceIds.includes(price.id)) {
                    return // Skip this price for this period
                  }
                }

                if (!grouped[p]) {
                  grouped[p] = []
                }
                grouped[p]!.push({
                  price,
                  product,
                  productType,
                })
              })
            } else if (
              period === 'one-time' &&
              productType === 'additional' &&
              product.id === 'prod_SY1S8CosHpGm5b'
            ) {
              // Special handling for Activation Fee - appears in all periods
              const allPeriods: BillingPeriod[] = [
                'monthly',
                'quarterly',
                'yearly',
              ]
              allPeriods.forEach((p) => {
                // Check if this price is allowed for each specific period
                if (defaultPricesForProduct) {
                  const allowedPriceIds = defaultPricesForProduct[p]
                  if (allowedPriceIds && !allowedPriceIds.includes(price.id)) {
                    return // Skip this price for this period
                  }
                }

                if (!grouped[p]) {
                  grouped[p] = []
                }
                grouped[p]!.push({
                  price,
                  product,
                  productType,
                })
              })
            } else {
              if (defaultPricesForProduct) {
                const allowedPriceIds = defaultPricesForProduct[period]
                if (allowedPriceIds && !allowedPriceIds.includes(price.id)) {
                  return // Skip this price
                }
              }

              if (!grouped[period]) {
                grouped[period] = []
              }

              grouped[period]!.push({
                price,
                product,
                productType,
              })
            }
          })
      })
    }

    // Process all products with their types
    // Order: base product, then additional products, then add-ons at the end
    if (baseProduct) {
      processProducts([baseProduct], 'base')
    }
    processProducts(additionalProducts, 'additional')
    processProducts(addonProducts, 'addon')

    return grouped
  }

  const groupedByPeriod = groupPricesByPeriod()

  const availablePeriods: BillingPeriod[] = []
  if (baseProduct) {
    const baseProductDefaultPrices = defaultPriceIds[baseProduct.id]
    if (baseProductDefaultPrices) {
      // Get periods in the order we want to display them
      const periodOrder: BillingPeriod[] = [
        'monthly',
        'quarterly',
        'semi_annual',
        'yearly',
        'one-time',
      ]
      periodOrder.forEach((period) => {
        if (baseProductDefaultPrices[period] && groupedByPeriod[period]) {
          availablePeriods.push(period)
        }
      })
    }
  }

  const periodLabels: { [key in BillingPeriod]: string } = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semi_annual: 'Semi-Annual',
    yearly: 'Yearly',
    'one-time': 'One-Time',
  }

  // Load saved selections from localStorage when modal opens
  useEffect(() => {
    if (show) {
      const initialQuantities: { [priceId: string]: number } = {}
      // Set default quantity to 1 for all prices
      Object.values(groupedByPeriod).forEach((pricesInPeriod) => {
        pricesInPeriod?.forEach((item) => {
          initialQuantities[item.price.id] = 1
        })
      })

      // If in edit mode, pre-populate with existing items
      if (isEditMode && existingItems.length > 0) {
        const editQuantities: { [priceId: string]: number } = {}
        const editActiveProducts = new Set<string>()
        let editSapphirePriceId: string | null = null
        let detectedPeriod: BillingPeriod | null = null

        let editTextingBettyPriceId: string | null = null
        let editFindMyPriceId: string | null = null
        existingItems.forEach((item) => {
          editQuantities[item.priceId] = item.quantity
          editActiveProducts.add(item.productId)

          // Check if this is a Sapphire product
          if (item.productId === LINQ_SAPPHIRE_PRODUCT_ID) {
            editSapphirePriceId = item.priceId
          }

          // Check if this is a Texting Betty product
          if (item.productId === TEXTING_BETTY_PRODUCT_ID) {
            editTextingBettyPriceId = item.priceId
          }

          // Check if this is a Find My product
          if (item.productId === LINQ_FIND_MY_PRODUCT_ID) {
            editFindMyPriceId = item.priceId
          }

          // Detect the billing period from the first item
          if (!detectedPeriod) {
            const price = Object.values(prices)
              .flat()
              .find((p) => p.id === item.priceId)
            if (price) {
              if (price.type === 'recurring' && price.recurring) {
                const { interval, interval_count: intervalCount } =
                  price.recurring
                if (interval === 'month') {
                  if (intervalCount === 1) detectedPeriod = 'monthly'
                  else if (intervalCount === 3) detectedPeriod = 'quarterly'
                  else if (intervalCount === 6) detectedPeriod = 'semi_annual'
                } else if (interval === 'year' && intervalCount === 1) {
                  detectedPeriod = 'yearly'
                }
              } else if (price.type === 'one_time') {
                detectedPeriod = 'one-time'
              }
            }
          }
        })

        setQuantities({ ...initialQuantities, ...editQuantities })
        setActiveProducts(editActiveProducts)
        setActiveSapphirePriceId(editSapphirePriceId)
        setActiveTextingBettyPriceId(editTextingBettyPriceId)
        setActiveFindMyPriceId(editFindMyPriceId)
        if (detectedPeriod && availablePeriods.includes(detectedPeriod)) {
          setSelectedPeriod(detectedPeriod)
        }
      } else {
        setQuantities(initialQuantities)

        // Try to load saved selections from localStorage
        try {
          const savedActiveProducts = localStorage.getItem(
            `modal-active-products-${productName}`
          )
          const savedSapphirePrice = localStorage.getItem(
            `modal-sapphire-price-${productName}`
          )
          const savedPeriod = localStorage.getItem(
            `modal-billing-period-${productName}`
          )

          if (savedActiveProducts) {
            const parsed = JSON.parse(savedActiveProducts)
            setActiveProducts(new Set(parsed))
          } else {
            setActiveProducts(new Set())
          }

          // Load tiered addon selections
          setActiveSapphirePriceId(savedSapphirePrice || null)
          setActiveTextingBettyPriceId(
            localStorage.getItem(`modal-texting-betty-price-${productName}`) ||
              null
          )

          // Load saved billing period if available
          if (
            savedPeriod &&
            availablePeriods.includes(savedPeriod as BillingPeriod)
          ) {
            setSelectedPeriod(savedPeriod as BillingPeriod)
          } else if (availablePeriods.length > 0) {
            setSelectedPeriod(availablePeriods[0])
          }
        } catch (error) {
          console.error('Error loading modal selections:', error)
          setActiveProducts(new Set())
          setActiveSapphirePriceId(null)
          if (availablePeriods.length > 0) {
            setSelectedPeriod(availablePeriods[0])
          }
        }
      }
    }
  }, [show, isEditMode, existingItems])

  // Deselect tiered addon products if their selected tier becomes unavailable when switching billing periods
  useEffect(() => {
    const checkAndDeselect = (
      productId: string,
      activeState: string | null,
      setActiveState: (v: string | null) => void
    ) => {
      if (!activeState) return
      const allowedPriceIds = defaultPriceIds[productId]?.[selectedPeriod] || []
      if (!allowedPriceIds.includes(activeState)) {
        setActiveState(null)
        setActiveProducts((prev) => {
          const newActive = new Set(prev)
          newActive.delete(productId)
          return newActive
        })
      }
    }

    checkAndDeselect(
      LINQ_SAPPHIRE_PRODUCT_ID,
      activeSapphirePriceId,
      setActiveSapphirePriceId
    )
    checkAndDeselect(
      TEXTING_BETTY_PRODUCT_ID,
      activeTextingBettyPriceId,
      setActiveTextingBettyPriceId
    )
  }, [selectedPeriod, activeSapphirePriceId, activeTextingBettyPriceId])

  // Save active products to localStorage
  useEffect(() => {
    if (show) {
      try {
        localStorage.setItem(
          `modal-active-products-${productName}`,
          JSON.stringify(Array.from(activeProducts))
        )
      } catch (error) {
        console.error('Error saving active products:', error)
      }
    }
  }, [activeProducts, show, productName])

  // Save tiered addon selections to localStorage
  useEffect(() => {
    if (show) {
      try {
        const tieredSelections: { [key: string]: string | null } = {
          sapphire: activeSapphirePriceId,
          'texting-betty': activeTextingBettyPriceId,
        }
        Object.entries(tieredSelections).forEach(([key, value]) => {
          const storageKey = `modal-${key}-price-${productName}`
          if (value) {
            localStorage.setItem(storageKey, value)
          } else {
            localStorage.removeItem(storageKey)
          }
        })
      } catch (error) {
        console.error('Error saving tiered addon selections:', error)
      }
    }
  }, [activeSapphirePriceId, activeTextingBettyPriceId, show, productName])

  // Save billing period to localStorage
  useEffect(() => {
    if (show) {
      try {
        localStorage.setItem(
          `modal-billing-period-${productName}`,
          selectedPeriod
        )
      } catch (error) {
        console.error('Error saving billing period:', error)
      }
    }
  }, [selectedPeriod, show, productName])

  const handleQuantityChange = (priceId: string, quantity: number) => {
    const clamped = Math.max(0, Math.min(999, quantity))

    // Check if this price belongs to a base product
    const currentPeriodPrices = groupedByPeriod[selectedPeriod] || []
    const changedItem = currentPeriodPrices.find(
      (item) => item.price.id === priceId
    )

    if (changedItem && changedItem.productType === 'base') {
      // If base product quantity changes, sync all addon and additional products
      // except Partner API Support which should remain constant
      const updatedQuantities: { [priceId: string]: number } = {
        ...quantities,
        [priceId]: clamped,
      }

      currentPeriodPrices.forEach((item) => {
        if (
          (item.productType === 'addon' || item.productType === 'additional') &&
          item.product.id !== PARTNER_API_SUPPORT_PRODUCT_ID
        ) {
          updatedQuantities[item.price.id] = clamped
        }
      })

      setQuantities(updatedQuantities)
    } else {
      setQuantities((prev) => ({ ...prev, [priceId]: clamped }))
    }
  }

  const toggleProduct = (productId: string) => {
    const newActive = new Set(activeProducts)
    let newOrder = [...addonOrder]

    if (newActive.has(productId)) {
      newActive.delete(productId)
      // Remove from order
      newOrder = newOrder.filter((id) => id !== productId)
    } else {
      newActive.add(productId)
      // Add to end of order
      if (!newOrder.includes(productId)) {
        newOrder.push(productId)
      }
    }

    setActiveProducts(newActive)
    setAddonOrder(newOrder)
  }

  // Get current period's prices filtered by active products
  const allPrices = groupedByPeriod[selectedPeriod] || []

  // Separate into base, additional, and addons
  const basePrices = allPrices.filter((item) => item.productType === 'base')
  const additionalPrices = allPrices.filter(
    (item) => item.productType === 'additional'
  )
  const addonPrices = allPrices.filter((item) => {
    // Special handling for tiered addon products - only show the selected tier
    const tieredConfig = tieredAddonProducts[item.product.id]
    if (tieredConfig) {
      if (!tieredConfig.activeState) return false
      return item.price.id === tieredConfig.activeState
    }
    // For other addon products, check if they are active
    return item.productType === 'addon' && activeProducts.has(item.product.id)
  })

  // Sort addons by selection order
  const sortedAddonPrices = addonPrices.sort((a, b) => {
    const indexA = addonOrder.indexOf(a.product.id)
    const indexB = addonOrder.indexOf(b.product.id)
    return indexA - indexB
  })

  // Combine in order: base, additional, addons (in selection order)
  const currentPrices = [
    ...basePrices,
    ...additionalPrices,
    ...sortedAddonPrices,
  ]

  const handleAddProducts = () => {
    const items: {
      priceId: string
      quantity: number
      productId: string
      discount?: number
    }[] = []

    // Iterate over current visible prices
    currentPrices.forEach((item) => {
      const quantity = quantities[item.price.id] ?? 1
      if (quantity > 0) {
        items.push({
          priceId: item.price.id,
          productId: item.product.id,
          quantity,
        })
      }
    })

    if (items.length > 0) {
      onAddProducts(items)
      onHide()
    }
  }

  const formatPrice = (price: Price): string => {
    // First preference: unit_amount
    if (price.unit_amount !== null && price.unit_amount !== undefined) {
      const amount = price.unit_amount / 100
      return `$${amount.toFixed(2)}`
    }

    // Fallback: use metadata description (extract just the price part)
    if (price.metadata?.internal_description) {
      const description = price.metadata.internal_description
      if (description.includes('Starts at')) {
        return description
      }
      // Extract just the price part (e.g., "$249.00" from "$249.00 / year")
      const priceMatch = description.match(/\$[\d,]+\.?\d*/)
      if (priceMatch) {
        return priceMatch[0]
      }
      // If no price pattern found, return the whole description
      return description
    }

    // Last resort
    return '$0.00'
  }

  const formatPeriod = (price: Price): string => {
    if (price.type === 'recurring' && price.recurring) {
      const { interval, interval_count: intervalCount } = price.recurring
      if (intervalCount === 1) return interval
      return `${intervalCount} ${interval}s`
    }
    return 'one-time'
  }

  // Count items to add based on actual quantity values (using default of 1 if not set)
  const totalItemsToAdd = currentPrices.filter((item) => {
    const quantity = quantities[item.price.id] ?? 1
    return quantity > 0
  }).length

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header className="bg-dark text-white border-0">
        <Modal.Title className="fs-5 fw-semibold">{productName}</Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="bg-dark text-white p-4"
        style={{ maxHeight: 'calc(100vh - 250px)' }}
      >
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2">Loading products...</div>
          </div>
        ) : (
          <>
            {/* Billing Period Tabs */}
            {availablePeriods.length > 1 && (
              <div
                className="d-flex flex-wrap gap-2 p-2 mb-4 rounded-3"
                style={{
                  backgroundColor: '#1f2937',
                  width: 'fit-content',
                }}
              >
                {availablePeriods.map((period) => (
                  <Button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className="border-0 rounded-3 px-4 fw-semibold small"
                    style={{
                      backgroundColor:
                        selectedPeriod === period
                          ? productColor
                          : 'transparent',
                      color: selectedPeriod === period ? '#ffffff' : '#9ca3af',
                      padding: '10px 24px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {periodLabels[period]}
                  </Button>
                ))}
              </div>
            )}

            {/* Addon Product Pills */}
            {addonProducts.length > 0 && (
              <div className="d-flex gap-2 mb-3 flex-wrap">
                {addonProducts.map((product) => {
                  // Check if this is a tiered addon product (Sapphire, Texting Betty, etc.)
                  const tieredConfig = tieredAddonProducts[product.id]
                  if (tieredConfig) {
                    // Each tiered addon is restricted to specific base products
                    const tieredBaseProductIds =
                      product.id === LINQ_FIND_MY_PRODUCT_ID
                        ? [
                            LINQ_INFRA_PRODUCT_ID,
                            LINQ_INFRA_200_CONTACT_PRODUCT_ID,
                          ]
                        : [LINQ_BLUE_PRODUCT_ID]
                    if (
                      !baseProduct ||
                      !tieredBaseProductIds.includes(baseProduct.id)
                    ) {
                      return null
                    }

                    // Filter tiers based on selected billing period
                    const allowedPriceIds =
                      defaultPriceIds[product.id]?.[selectedPeriod] || []
                    const availableTiers = tieredConfig.tiers.filter((tier) =>
                      allowedPriceIds.includes(tier.priceId)
                    )

                    // Don't show if no tiers available for this period
                    if (availableTiers.length === 0) {
                      return null
                    }

                    return availableTiers.map((tier) => {
                      const isActive = tieredConfig.activeState === tier.priceId
                      return (
                        <Button
                          key={tier.priceId}
                          onClick={() => {
                            if (tieredConfig.activeState === tier.priceId) {
                              tieredConfig.setActiveState(null)
                              const newActive = new Set(activeProducts)
                              newActive.delete(product.id)
                              setActiveProducts(newActive)
                              // Remove from addon order
                              setAddonOrder(
                                addonOrder.filter((id) => id !== product.id)
                              )
                            } else {
                              tieredConfig.setActiveState(tier.priceId)
                              const newActive = new Set(activeProducts).add(
                                product.id
                              )
                              setActiveProducts(newActive)
                              // Add to addon order if not already there
                              if (!addonOrder.includes(product.id)) {
                                setAddonOrder([...addonOrder, product.id])
                              }
                            }
                          }}
                          className={`rounded-2 px-2 py-1 fw-semibold small ${
                            isActive ? 'active' : ''
                          }`}
                          style={{
                            backgroundColor: isActive
                              ? productColor
                              : getInactiveBackgroundColor(productColor),
                            color: isActive ? '#ffffff' : productColor,
                            border: `2px solid ${
                              isActive
                                ? '#ffffff'
                                : getLighterColor(productColor, 0.3)
                            }`,
                            transition: 'all 0.2s',
                            fontSize: '13px',
                          }}
                        >
                          {tier.label} {isActive ? '+' : ''}
                        </Button>
                      )
                    })
                  }

                  // Regular addon products
                  const isActive = activeProducts.has(product.id)
                  const productName =
                    product.metadata?.display_name || product.name
                  return (
                    <Button
                      key={product.id}
                      onClick={() => toggleProduct(product.id)}
                      className={`rounded-2 px-2 py-1 fw-semibold small ${
                        isActive ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: isActive
                          ? productColor
                          : getInactiveBackgroundColor(productColor),
                        color: isActive ? '#ffffff' : productColor,
                        border: `2px solid ${
                          isActive
                            ? '#ffffff'
                            : getLighterColor(productColor, 0.3)
                        }`,
                        transition: 'all 0.2s',
                        fontSize: '13px',
                      }}
                    >
                      {productName} {isActive ? '+' : ''}
                    </Button>
                  )
                })}
              </div>
            )}

            {/* Product List */}
            <div>
              {currentPrices.length === 0 ? (
                <div className="text-center py-5" style={{ color: '#9ca3af' }}>
                  No products available for this billing period
                </div>
              ) : (
                currentPrices.map((item) => {
                  const displayName = item.product.name
                  const quantity = quantities[item.price.id] ?? 1

                  return (
                    <div
                      key={item.price.id}
                      className="rounded-2 p-3 mb-3 d-flex align-items-center justify-content-between"
                      style={{
                        backgroundColor:
                          item.productType === 'base' ? '#374151' : '#1f2937',
                        border: '1px solid #374151',
                        borderLeft:
                          item.productType !== 'base'
                            ? '3px solid #3b82f6'
                            : '1px solid #374151',
                      }}
                    >
                      <div className="flex-grow-1">
                        <div className="fw-semibold text-white d-flex align-items-center gap-2">
                          {displayName}
                        </div>
                        <div
                          className="small mt-1"
                          style={{ color: '#9ca3af' }}
                        >
                          {formatPrice(item.price)} / {formatPeriod(item.price)}
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2 ms-3">
                        <span
                          className="small me-2"
                          style={{ color: '#9ca3af' }}
                        >
                          Qty
                        </span>
                        <Button
                          size="sm"
                          onClick={() =>
                            handleQuantityChange(item.price.id, quantity - 1)
                          }
                          disabled={quantity === 0}
                          className="rounded-2 d-flex align-items-center justify-content-center text-white fw-semibold p-0"
                          style={{
                            width: '36px',
                            height: '36px',
                            border: '1px solid #4b5563',
                            backgroundColor: '#374151',
                            fontSize: '20px',
                          }}
                        >
                          −
                        </Button>
                        <div
                          className="rounded-2 text-center d-flex align-items-center justify-content-center text-white fw-semibold"
                          style={{
                            width: '50px',
                            height: '36px',
                            backgroundColor: '#1f2937',
                            border: '1px solid #4b5563',
                            fontSize: '16px',
                          }}
                        >
                          {quantity}
                        </div>
                        <Button
                          size="sm"
                          onClick={() =>
                            handleQuantityChange(item.price.id, quantity + 1)
                          }
                          className="rounded-2 d-flex align-items-center justify-content-center text-white fw-semibold p-0"
                          style={{
                            width: '36px',
                            height: '36px',
                            border: '1px solid #4b5563',
                            backgroundColor: '#374151',
                            fontSize: '20px',
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer
        className="bg-dark border-0 px-4"
        style={{ paddingTop: '20px', paddingBottom: '20px' }}
      >
        <Button
          variant="secondary"
          onClick={onHide}
          className="rounded-2 fw-semibold"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #4b5563',
            color: '#9ca3af',
            padding: '12px 32px',
            fontSize: '16px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddProducts}
          disabled={totalItemsToAdd === 0 || isLoading}
          className="rounded-2 border-0 text-white fw-semibold"
          style={{
            backgroundColor: '#3b82f6',
            padding: '12px 32px',
            fontSize: '16px',
          }}
        >
          {isEditMode ? 'Update' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ProductBundleModal
